/**
 * Post-auth Home — the landing after sign-in, rebuilt as a real, scannable dashboard rather than a bare
 * pair of links:
 *
 *   - a welcoming header (greeting + role/email),
 *   - a PROMINENT, inviting search affordance (the omnibox is the primary entry point — search-centric
 *     for a Viewer who has no worklists),
 *   - a row of worklist STAT TILES (renewals due / churn risk / upsell / underutilized) — big tabular
 *     numbers + semantic accent, each click-through to that list — shown ONLY for roles with
 *     worklist:read (gated by PermissionGate; the server enforces too), and
 *   - a compact "quick access" section so the viewport is filled with useful entry points, not a void.
 *
 * The worklist counts come from ONE request (useWorklistSummary → GET /worklists/summary) instead of the
 * five parallel per-kind fetches the Worklists page (DashboardPage) still uses for its row tables — the
 * summary endpoint returns counts only, so per-kind row reads still go through useAllWorklists elsewhere.
 * The hook is disabled for roles without worklist:read, so it simply stays idle and the tiles are gated
 * out.
 *
 * The summary also carries `refreshed_at`/`stale` for `mv_account_360` (the worklist matview) — a
 * DIFFERENT freshness fact from the ZPCD mirror. It renders through `FreshnessBadge kind="worklist"`
 * beside the section heading so a paused/failed refresh is visible, never silently served as live.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ChevronRight, Search } from "lucide-react";
import { useRbac } from "@/context/RbacContext";
import { PermissionGate } from "@/components/portal/PermissionGate";
import { FreshnessBadge } from "@/components/portal/FreshnessBadge";
import { Modal, PendingNumber } from "@/components/ui";
import { formatInt } from "@/lib/format";
import type { WorklistKind } from "@/api/types";
import { WORKLIST_KINDS, useWorklistSummary } from "@/features/worklists/useWorklists";
import { WorklistTable } from "@/features/worklists/WorklistTable";
import { KIND_PRESENTATION, medallionClass } from "@/features/worklists/kindPresentation";
import { cmdKLabel } from "@/lib/platform";
import { cn } from "@/lib/cn";

/** Fallback display name derived from the sign-in email when /me carries no real name. */
function nameFromEmail(email: string): string {
  const local = email.split("@")[0] || email;
  return (
    local
      .split(/[._-]+/)
      .filter(Boolean)
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(" ") || email
  );
}

export function HomePage() {
  const { t } = useTranslation();
  const { me, role, hasPermission } = useRbac();
  // Safe to call unconditionally: the hook is disabled for roles without worklist:read (idle, no fetch),
  // and the stat tiles below are gated by PermissionGate regardless.
  const summary = useWorklistSummary();
  const canSearch = hasPermission("search:read");
  // Clicking a worklist tile opens that list in a modal (no full-page jump); "View all" still routes to
  // the Dashboard.
  const [modalKind, setModalKind] = useState<WorklistKind | null>(null);

  return (
    <section aria-labelledby="home-title" className="flex flex-col gap-8">
      <header className="flex flex-col gap-1">
        <h1 id="home-title" className="text-(length:--text-2xl) font-semibold text-(--text-strong)">
          {t("home.greeting")}, {me.name ?? nameFromEmail(me.email)}
        </h1>
        <p className="text-(length:--text-sm) text-(--text-muted)">{t(`role.${role}`)}</p>
      </header>

      {/* Prominent, inviting search — the primary entry point (search-centric for Viewer). */}
      {canSearch && (
        <Link
          to="/search"
          aria-label={t("home.search.title")}
          className={cn(
            "group flex items-center gap-2.5 rounded-(--radius) border border-(--border) bg-(--surface) px-3.5 py-2.5 shadow-(--shadow-sm)",
            "transition-[border-color,box-shadow] hover:border-(--border-strong) hover:shadow-(--shadow-md)",
            "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--brand)",
          )}
        >
          <Search className="size-4 shrink-0 text-(--text-subtle)" aria-hidden />
          <span className="min-w-0 flex-1 truncate text-(length:--text-sm) text-(--text-placeholder)">
            {t("home.search.title")}
          </span>
          <kbd className="tabular hidden shrink-0 rounded-(--radius-sm) border border-(--border) bg-(--surface-sunken) px-1.5 py-0.5 text-(length:--text-2xs) font-medium text-(--text-subtle) sm:inline-block">
            {cmdKLabel()}
          </kbd>
        </Link>
      )}

      {/* Worklist stat tiles — only for roles that hold worklist:read. */}
      <PermissionGate permission="worklist:read">
        <section aria-labelledby="home-worklist-title" className="flex flex-col gap-3">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1.5">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1.5">
              <h2
                id="home-worklist-title"
                className="text-(length:--text-base) font-semibold text-(--text-strong)"
              >
                {t("home.worklist.heading")}
              </h2>
              {/* mv_account_360 refresh freshness — a DIFFERENT fact from the ZPCD mirror badges; only
                  rendered once the summary has landed (no request yet = nothing honest to show). */}
              {summary.data && (
                <FreshnessBadge
                  kind="worklist"
                  refreshedAt={summary.data.refreshed_at}
                  stale={summary.data.stale}
                />
              )}
            </div>
            <Link
              to="/worklists"
              className="text-(length:--text-xs) font-medium text-(--brand-strong) hover:underline"
            >
              {t("home.worklist.viewAll")}
            </Link>
          </div>
          {/* r10 22/7: auto-fit, same fix + rationale as the Dashboard health band — no fixed column cap
              now that the shell is full-bleed, but never narrower than a tile can hold its label. */}
          <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-3">
            {WORKLIST_KINDS.map((kind) => {
              const count = summary.isError ? null : (summary.data?.counts[kind] ?? null);
              return (
                <StatTile
                  key={kind}
                  kind={kind}
                  count={count}
                  pending={summary.isPending}
                  onOpen={() => setModalKind(kind)}
                />
              );
            })}
          </div>
        </section>
      </PermissionGate>

      <Modal
        open={modalKind !== null}
        onClose={() => setModalKind(null)}
        title={modalKind ? t(`worklist.${modalKind}`) : ""}
        size="xl"
      >
        {modalKind && <WorklistTable kind={modalKind} />}
      </Modal>
    </section>
  );
}

interface StatTileProps {
  kind: WorklistKind;
  count: number | null;
  pending: boolean;
  onOpen: () => void;
}

/** A worklist stat tile — the shared kindPresentation.ts icon/tone (same truth as the Dashboard health
 *  band) and a big tabular count. The medallion is neutral until the count is actually > 0 — colour = a
 *  LIVE status, not a permanent category label. Clicking opens that list in a modal. */
function StatTile({ kind, count, pending, onOpen }: StatTileProps) {
  const { t } = useTranslation();
  const { icon, tone, labelKey } = KIND_PRESENTATION[kind];
  const active = !pending && count != null && count > 0;
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "group flex items-center gap-4 rounded-(--radius) border border-(--border) bg-(--surface) p-4 text-left shadow-(--shadow-sm)",
        "transition-[border-color,box-shadow] hover:border-(--border-strong) hover:shadow-(--shadow-md)",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--brand)",
      )}
    >
      <span
        className={cn(
          "grid size-12 shrink-0 place-items-center rounded-(--radius-sm)",
          medallionClass(tone, active),
        )}
        aria-hidden
      >
        {icon}
      </span>
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="tabular text-(length:--text-2xl) font-semibold leading-none text-(--text-strong)">
          {pending ? (
            <PendingNumber />
          ) : count == null ? (
            <span className="text-(--text-subtle)">—</span>
          ) : (
            formatInt(count)
          )}
        </span>
        <span className="line-clamp-2 text-(length:--text-sm) font-medium leading-snug text-(--text-muted)">
          {t(labelKey)}
        </span>
      </span>
      <ChevronRight
        className="ml-auto size-4 shrink-0 text-(--text-subtle) opacity-0 transition-opacity group-hover:opacity-100"
        aria-hidden
      />
    </button>
  );
}
